import dayjs from 'dayjs';
import { BACKEND_URL } from '@/core/config';
import { server } from '../core/server/axios';
import type { RequestModel } from '@/pages/HistoryPage/components/RequestListPage/RequestListPage';

interface NewRequestVariables {
    iin: string
    phone: string
    fullName: string
    currencyFrom: string
    currencyTo: string
    value: string | number
    rate: string | number
    amount: string | number
    isIndividual?: boolean
    kassaId: number
    depId: number
    isReserve: 1 | 0
    isDelivery: 0
    businessKey: string
}

interface NewRequestVariablesBlack {
    iin: string,
    phone: string,
    fullName: string,
    birthDate: any,
    docType: string,
    series: string,
    docNumber: string,
    docIssue: any,
    citizen: string,
    citizenCode: string,
    currencyFrom: string,
    currencyTo: string,
    value: string | number,
    rate: number,
    amount: string | number,
    kassaId: string | number,
    depId: string | number,
    actionType: "buy" | "sell",
    date: any,
    businessKey: string,
    initiator: string
}

interface UploadApplicationVariables {
    requestId: string | number,
    formData: FormData,
}

interface SearchRequestVariables {
    businessKey?: string,
    status?: number,
    iin?: string,
    amount?: string,
    currencyCode?: string,
    actionType?: string,
    lastname?: string,
    firstname?: string,
    middlename?: string,
    phone?: string,
    startDate?: Date,
    endDate?: Date,
    depId?: string[] | number[],
    initiator?: string,
    afmStatus?: number, // 0 - Не нужно отправлять, 1 - Нужно отправить, 2 - Сохранено, 3 - Подписано ЭЦП
    fromSum?: number,
    toSum?: number
}

export interface AfmReconciliationItem {
    date: string;
    depId: number;
    depName: string;
    requestId: number;
    afmDocId: number;
    cabinetFormNumber: number;
    afmId: number;
    operationDate: string;
    dealType: string;
    value: number;
    currency: string;
    fullName: string;
    initiator: string;
    afmStatus: number;
    afmStatusName: string;
    status: number;
    statusName: string;
    statusCode: string;
    issue: string | null;
    issueName: string | null;
    isDataDefectOnly: boolean;
    resolvedAt: string | null;
    state: string;
    firstSeenAt: string;
    checkedAt: string;
}

export interface AfmReconciliationFilterOption {
    value: number;
    code: string | null;
    name: string;
}

export interface AfmReconciliationSummary {
    total: number;
    withIssue: number;
    openIssues: number;
    resolvedIssues: number;
    byStatus: Record<string, number>;
}

export interface AfmReconciliationResponse {
    items: AfmReconciliationItem[];
    page: number;
    size: number | null;
    totalCount: number;
    totalPages: number;
    summary: AfmReconciliationSummary;
    filters: {
        deps: AfmReconciliationFilterOption[];
        statuses: AfmReconciliationFilterOption[];
    };
}

export interface AfmShiftCheckItem {
    requestId: number;
    afmDocId: number;
    cabinetFormNumber: number | null;
    afmId: number | null;
    afmStatus: number; // 0 - Не нужно отправлять, 1 - Нужно отправить, 2 - Сохранено, 3 - Подписано ЭЦП
    afmStatusName: string;
    cabinetStatus: number | null;
    cabinetStatusName: string | null;
    hasSign: boolean | null;
    isSent: boolean;
    issue: number | null;
    issueName: string | null;
}

export interface AfmShiftCheckReport {
    reportId: string;
    kassaId: number;
    depId: number;
    depName: string;
    shiftOpen: string;
    shiftClose: string | null;
    ok: boolean;
    message: string;
    error: string | null;
    totalRequests: number;
    sentCount: number;
    cabinetDocs: number;
    unreadableDocs: number;
    notSent: AfmShiftCheckItem[];
    items: AfmShiftCheckItem[];
}

/** Один шаг жизни заявки: создание, изменение, проведение, отмена */
export interface RequestLogStep {
    step: number;
    stepName: string;
    /** Уже локальное время в виде «21.09.2026 10:02:37» — не ISO, dayjs без формата его не разберёт */
    date: string;
    login: string;
    status: string;
    rate: number;
    amount: number;
    value: number;
    kassaId: number;
    /** «Да» / «Нет» строкой */
    isNewClient: string;
    /** Готовый к выводу текст: «Заявка создана», «Статус: было «…», стало «…»» */
    changes: string;
    comment: string | null;
}

export interface RequestLogModel {
    requestId: number;
    stepsCount: number;
    currentStatus: string;
    /** Код валюты заявки: «USD», «GOLD5» */
    currency?: string | null;
    /** Готовый к выводу текст: «Покупка» / «Продажа» */
    dealType?: string | null;
    steps: RequestLogStep[];
}

export interface RequestLogsFilter {
    /** YYYY-MM-DD, границы включительно */
    from: string;
    to: string;
    /** Обязателен: без DepId бэк отвечает 400 «The DepId field is required» */
    depId: number[];
    kassaId?: number | null;
    /** Код статуса заявки, как в истории операций: 0 — введен, 1 — исполнен, 2 — возврат, 3 — отменён */
    status?: number | null;
    iin?: string | null;
    currencyCode?: string | null;
    actionType?: "sell" | "buy" | null;
}

/**
 * Query-параметры request-logs. Отделения уходят повторами DepId=1&DepId=2 (indexes: null):
 * скобочный вид DepId[]=1, который axios шлёт по умолчанию, бэк не разбирает и отдаёт лишние точки.
 */
const requestLogsConfig = (filter: RequestLogsFilter) => ({
    baseURL: BACKEND_URL,
    params: {
        from: filter.from,
        to: filter.to,
        DepId: filter.depId,
        KassaId: filter.kassaId || undefined,
        Status: filter.status ?? undefined,
        Iin: filter.iin || undefined,
        CurrencyCode: filter.currencyCode || undefined,
        ActionType: filter.actionType || undefined,
    },
    paramsSerializer: { indexes: null },
});

export class RequestController {
    /**
     * История изменений заявок за период — по шагу на каждое действие.
     * В отличие от остальных ручек ECASH принимает голую дату YYYY-MM-DD (проверено: 200 на from=to=день),
     * а не UTC-момент.
     */
    async getRequestLogs(filter: RequestLogsFilter): Promise<RequestLogModel[]> {
        return await server.get('/Application/request-logs', requestLogsConfig(filter));
    }

    /** Тот же отчёт файлом .xlsx */
    async exportRequestLogs(filter: RequestLogsFilter): Promise<Blob> {
        return await server.get('/Application/request-logs/excel', {
            ...requestLogsConfig(filter),
            responseType: "blob",
        });
    }

    async newRequest(request: NewRequestVariables) {
        return await server.post('/Application/NewRequest', request, {
            baseURL: BACKEND_URL
        });
    }

    async updateRequest(request: {
        requestId: number | string,
        amount: number,
        value: string,
        rate: string,
    }) {
        return await server.post('/Application/UpdateRequest', { ...request, isTechnical: 0 }, {
            baseURL: BACKEND_URL
        });
    }

    async uploadApplication(request: UploadApplicationVariables) {
        return await server.post(`/Application/uploadApplication/${request.requestId}`, request.formData, {
            baseURL: BACKEND_URL
        });
    }

    async checkIsLast({ requestId, kassaId }: { requestId: number, kassaId: number }) {
        return await server.get(`/Application/isLast/${requestId}/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async returnRequestQuiq(requestId: number) {
        return await server.get(`/Application/Return/${requestId}`, {
            baseURL: BACKEND_URL
        });
    }

    async returnRequestOfd({ requestId, businessKey }: { requestId: number, businessKey: string }) {
        return await server.get(`/EcashCore/tradeOperationReturn/${requestId}/${businessKey}`, {
            baseURL: BACKEND_URL
        });
    }

    async newRequestBlack(request: NewRequestVariablesBlack) {
        return await server.post('/Application/NewRequestBlack', request, {
            baseURL: BACKEND_URL
        });
    }

    async afmJson(requestId: number) {
        return await server.get(`/Application/afm/${requestId}`, {
            baseURL: BACKEND_URL
        });
    }

    async getAfmApp(kassaId: number) {
        return await server.get(`/Application/getAfmApp/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async sendAfmApp(request: number) {
        return await server.get(`/afm/send/${request}`, {
            baseURL: BACKEND_URL
        });
    }

    async checkOvertime(kassaId: number) {
        return await server.get(`/Application/checkEnteredAppl/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async searchRequest({
        businessKey,
        status,
        iin,
        amount,
        currencyCode,
        actionType,
        lastname,
        firstname,
        middlename,
        phone,
        startDate,
        endDate,
        depId,
        initiator,
        afmStatus,
        fromSum,
        toSum
    }: SearchRequestVariables) {
        return await server.post('/Application/search', {
            businessKey: businessKey || null,
            status: status || null,
            iin: iin || null,
            amount: amount || null,
            currencyCode: currencyCode || null,
            actionType: actionType || null,
            lastname: lastname || null,
            firstname: firstname || null,
            middlename: middlename || null,
            phone: phone || null,
            startDate: startDate || null,
            endDate: endDate || null,
            depId: depId || null,
            initiator: initiator || null,
            afmStatus: afmStatus || null,
            fromSum: fromSum || null,
            toSum: toSum || null
        }, {
            baseURL: BACKEND_URL,
        });
    }

    async checkClientDiscount(clientId: number | string): Promise<{ turnover: number, isDiscountAvailable: boolean }> {
        return await server.get(`/Application/checkIsDiscount/${clientId}`, {
            baseURL: BACKEND_URL
        });
    }

    async getAfmReconciliation(request: { dateTo: string, dateFrom: string, depIds: number[] }): Promise<AfmReconciliationResponse> {
        return await server.post(`/AFM/reconciliation/list`, request, {
            baseURL: BACKEND_URL
        });
    }

    async runAfmReconciliation(days = 2): Promise<AfmReconciliationItem[]> {
        const date = dayjs().format('YYYY-MM-DD');
        return await server.post(`/AFM/reconciliation/run?date=${date}&days=${days}`, {}, {
            baseURL: BACKEND_URL
        });
    }

    async exportAfmReconciliation(request: { dateTo: string, dateFrom: string, depIds: number[] }): Promise<Blob> {
        return await server.post(`/AFM/reconciliation/export`, request, {
            responseType: "blob",
            baseURL: BACKEND_URL
        });
    }

    async checkAfmReconciliation(reportId: string): Promise<AfmShiftCheckReport> {
        return await server.get(`/AFM/reconciliation/closeDay/${reportId}`, {
            responseType: "json",
            baseURL: BACKEND_URL
        });
    }

    async afmReconciliationOpenDay(kassaId: number): Promise<{ model: RequestModel[]; count: number; }> {
        return await server.get(`/AFM/reconciliation/openDay/${kassaId}`, {
            responseType: "json",
            baseURL: BACKEND_URL
        });
    }
}